import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";

export function CancelOrderModal({
  open,
  onOpenChange,
  onConfirm,
  submitting,
  orderNumber,
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="cancel-order-modal"
        className="max-w-md border-border-subtle bg-surface text-text-primary"
      >
        <DialogHeader>
          <DialogTitle>{t("orders.cancel_modal.title")}</DialogTitle>
          <DialogDescription className="text-text-secondary">
            {t("orders.cancel_modal.body")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2">
          {orderNumber && (
            <div
              className="rounded-md border border-border-subtle bg-offwhite/60 px-3 py-2 text-xs text-text-secondary"
              data-testid="cancel-order-number"
            >
              <span className="uppercase tracking-wide">
                {t("orders.columns.order_number")}
              </span>
              <span className="ml-2 font-mono text-sm font-semibold text-text-primary">
                #{orderNumber}
              </span>
            </div>
          )}
          <label
            htmlFor="cancel-reason"
            className="block text-xs font-medium text-text-secondary"
          >
            {t("orders.cancel_modal.reason_label")}
          </label>
          <Textarea
            id="cancel-reason"
            data-testid="cancel-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value.slice(0, 300))}
            placeholder={t("orders.cancel_modal.reason_placeholder")}
            className="min-h-[96px] bg-surface border-border-subtle focus-visible:ring-champagne"
            maxLength={300}
          />
          <div className="text-right text-[10px] text-text-secondary">
            {reason.length}/300
          </div>
        </div>

        <DialogFooter className="sm:justify-end">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            data-testid="cancel-order-cancel"
            className="border-border-subtle text-text-primary hover:bg-offwhite"
          >
            {t("orders.cancel_modal.cancel")}
          </Button>
          <Button
            onClick={() => onConfirm(reason.trim() || undefined)}
            disabled={submitting}
            data-testid="cancel-order-confirm"
            className="bg-status-unavailable text-white hover:bg-status-unavailable/90"
          >
            {submitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("common.loading")}
              </>
            ) : (
              t("orders.cancel_modal.confirm")
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
